import { MultiSelect } from "@/components/ui/multi-select"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import {
  FilterProperty,
  FilterType,
  FilterVariant,
  getFilterTypesByFilterVariant,
} from "@/utils/filtering"

export type FilterSelectState = { id: string; filterType: FilterType; value: string | string[] }

export type UpdateSelectState = (update: {
  id?: string
  filterType?: FilterType
  value?: string | string[]
}) => void

interface FilterFormRowProps {
  selectState: FilterSelectState
  updateSelectState: UpdateSelectState
  properties: FilterProperty[]
  fixedColumns?: boolean
}

const FilterFormRow = ({
  selectState,
  updateSelectState,
  properties,
  fixedColumns = false,
}: FilterFormRowProps) => {
  const selectedProperty = properties.find((p) => p.id === selectState.id)
  const filterVariant = selectedProperty?.filterVariant
  const singleValue = typeof selectState.value === "string" ? selectState.value : ""

  return (
    <div
      className={cn("flex flex-1 flex-wrap sm:flex-nowrap gap-2.5", fixedColumns && "items-start")}
    >
      <Select value={selectState.id} onValueChange={(value) => updateSelectState({ id: value })}>
        <SelectTrigger className={cn(fixedColumns && "sm:w-52 shrink-0")}>
          <SelectValue placeholder="Property" />
        </SelectTrigger>
        <SelectContent>
          {properties.map((property) => (
            <SelectItem value={property.id} key={`id-option-${property.id}`}>
              {property.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={selectState.filterType}
        onValueChange={(value) => updateSelectState({ filterType: value as FilterType })}
      >
        <SelectTrigger className={cn(fixedColumns && "sm:w-32 shrink-0")}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(selectedProperty?.filterTypes ?? getFilterTypesByFilterVariant(filterVariant)).map(
            (ft) => (
              <SelectItem value={ft} key={`id-filter-type-${ft}`}>
                {ft}
              </SelectItem>
            ),
          )}
        </SelectContent>
      </Select>
      {filterVariant === FilterVariant.MultiSelect && selectedProperty?.filterOptions ? (
        <MultiSelect
          className="flex-1 min-w-0"
          maxBadges={2}
          options={selectedProperty.filterOptions.map((option) => ({
            value: option,
            label: selectedProperty.filterOptionLabels?.[option] ?? option,
          }))}
          selected={Array.isArray(selectState.value) ? selectState.value : []}
          onChange={(values) => updateSelectState({ value: values })}
          placeholder="Values"
        />
      ) : filterVariant === FilterVariant.Select && selectedProperty?.filterOptions ? (
        <Select value={singleValue} onValueChange={(value) => updateSelectState({ value })}>
          <SelectTrigger className={cn("capitalize", fixedColumns && "flex-1 min-w-0")}>
            <SelectValue placeholder="Value" />
          </SelectTrigger>
          <SelectContent>
            {selectedProperty.filterOptions.map((option) => (
              <SelectItem value={option} key={`id-option-${option}`} className="capitalize">
                {selectedProperty.filterOptionLabels?.[option] ?? option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <input
          type={filterVariant === FilterVariant.Number ? "number" : "text"}
          placeholder="Value"
          className={cn(
            "rounded-md border border-palette-jungleGreen200 px-3 py-2 text-sm",
            fixedColumns && "flex-1 min-w-0",
          )}
          value={singleValue}
          onChange={(e) => updateSelectState({ value: e.target.value })}
        />
      )}
    </div>
  )
}

export default FilterFormRow
